/**
 * Search: the pop-up's suggestions, and the words shoppers use.
 *
 * <predictive-search data-suggestions>   (snippets/search-dialog.liquid)
 * Before he types, the pop-up shows the popular searches and the categories. While he types
 * it asks Shopify for matching products and collections and shows them under the box. When
 * nothing matches it says so, keeps the popular searches and the categories, and adds a row
 * of best sellers. Pressing Enter always goes to the full results page, so search works
 * without this script too.
 *
 * The words: a word the catalogue does not use ("تيشيرت", "فانيلة") is changed to the
 * catalogue's word before the store is asked (assets/search-synonyms.js), here, in every
 * search form of the theme and on every chip that starts a search. The pop-up says what it
 * looked for ("Showing results for “t-shirt”"). A results page that found nothing for such a
 * word, reached some other way, is asked again with the catalogue's word.
 */
import { parse, translate } from './search-synonyms.js';

const Kanze = window.Kanze;
const dialogSearch = () => document.querySelector('predictive-search');
/** The catalogue's words for what he typed, with the merchant's own pairs from the settings. */
const words = (typed) => translate(typed, parse(dialogSearch()?.dataset.synonyms));

/** An address for the same search with other words. */
function withTerms(address, terms) {
  const url = new URL(address, window.location.origin);
  url.searchParams.set('q', terms);
  if (!url.searchParams.has('type')) url.searchParams.set('type', 'product');
  return url;
}

// Every search form of the theme (the pop-up's, the results page's).
document.addEventListener('submit', (event) => {
  const form = event.target instanceof HTMLFormElement && event.target.matches('[data-search-form]') ? event.target : null;
  const typed = form?.elements.namedItem('q')?.value.trim();
  if (!typed) return;
  const { terms, changed } = words(typed);
  if (!changed) return;
  event.preventDefault();
  const url = new URL(form.action, window.location.origin);
  for (const [name, value] of new FormData(form)) url.searchParams.set(name, name === 'q' ? terms : value);
  window.location.assign(url);
});

// Every chip that starts a search (the popular searches).
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const chip = event.target instanceof Element ? event.target.closest('a[data-search-term]') : null;
  if (!chip) return;
  const { terms, changed } = words(chip.dataset.searchTerm);
  if (!changed) return;
  event.preventDefault();
  window.location.assign(withTerms(chip.href, terms));
});

// A results page with nothing on it, for words the table knows: ask again with the catalogue's.
const nothing = document.querySelector('[data-search-empty]');
if (nothing) {
  const { terms, changed } = words(nothing.dataset.terms || '');
  if (changed) window.location.replace(withTerms(window.location.href, terms));
}

/* ---------- What is counted (assets/kz-track.js, docs/design/WEB-TRACKING-PLAN.md) ---------- */

const track = (name, props, how) => window.kz?.track?.(name, props, how);
/** What he searched for, as the plan wants it: lower case, 40 characters at most. An email or a phone number typed there is not sent. */
const asked = (typed) => {
  const text = String(typed ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(text)) return '[email]';
  if (/(?:\+|\b00)\d[\d\s-]{6,}\d|(?<![\w-])\d{8,15}(?![\w-])|(?<![\w-])\d{4}[\s-]\d{4}(?![\w-])/.test(text)) return '[number]';
  return text.slice(0, 40);
};
/**
 * The same search is counted once, however it was made: typed and left to settle, then sent
 * with Enter. It is counted under the words he typed; `looked` is what the store was asked
 * for when those are not the same ("فانيلة" → "t-shirt"), which is what the results page knows.
 */
let counted = '';
function performed(typed, results, looked = typed) {
  const query = asked(typed);
  if (!query || query === counted) return;
  counted = query;
  try {
    const last = JSON.parse(sessionStorage.getItem('kz-searched'));
    if (last?.q?.includes(query) && Date.now() - last.t < 30000) return;
    sessionStorage.setItem('kz-searched', JSON.stringify({ q: [query, asked(looked)], t: Date.now() }));
  } catch (error) {
    /* Storage can be blocked: the search is counted anyway. */
  }
  track('Search Performed', { query, results_count: results });
}

// The results page: one search, made by sending the form.
const page = (() => {
  try {
    return JSON.parse(document.getElementById('KzTrack').textContent).search;
  } catch (error) {
    return null;
  }
})();
// (Not for a page that is about to ask again with the catalogue's words: see above.)
const asksAgain = Boolean(nothing) && words(nothing.dataset.terms || '').changed;
if (page?.performed && !asksAgain) performed(page.query, page.results_count);

// A result that is opened: in the suggestions under the box, or on the results page.
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href*="/products/"]') : null;
  if (!link) return;
  const box = link.closest('predictive-search');
  const list = box ? link.closest('[data-predictive-results]') : page?.performed ? link.closest('.search [data-grid]') : null;
  if (!list) return;
  const handle = decodeURIComponent(new URL(link.href).pathname.match(/\/products\/([^/?#]+)/)?.[1] || '');
  const all = [...new Set([...list.querySelectorAll('a[href*="/products/"]')].map((other) => new URL(other.href).pathname))];
  track('Search Result Tapped', { query: asked(box ? box.querySelector('[data-input]')?.value : page.query), position: all.indexOf(new URL(link.href).pathname) + 1, product_handle: handle }, { leaving: true });
});

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-input]');
    this.results = this.querySelector('[data-results]');
    this.start = this.querySelector('[data-start]');
    this.best = this.querySelector('[data-best]');
    this.line = this.querySelector('[data-showing-line]');
    if (!this.input || !this.results) return;

    this.closest('dialog')?.addEventListener('dialog:open', () => this.input.focus());
    this.querySelector('[data-clear]')?.addEventListener('click', () => {
      window.clearTimeout(this.timer);
      this.pending?.abort();
      this.pending = null;
      this.show(null);
      window.requestAnimationFrame(() => this.input.focus());
    });

    if (!this.hasAttribute('data-suggestions')) return;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      // He is still typing: the search has not settled.
      window.clearTimeout(this.settled);
      this.timer = window.setTimeout(() => this.search(this.input.value.trim()), 200);
    });
  }

  /**
   * Puts an answer under the box, or the start again when there is none.
   * @param {Element|null} answer - The section's `[data-predictive-results]`
   * @param {string} [looked] - The catalogue's words, when they are not the ones he typed
   */
  show(answer, looked = '') {
    const empty = Boolean(answer?.querySelector('[data-empty]'));
    this.results.replaceChildren(...(answer ? [document.importNode(answer, true)] : []));
    if (this.line) {
      this.line.hidden = !answer || !looked;
      this.line.textContent = looked ? Kanze.digits(this.dataset.showing.replace('[terms]', looked)) : '';
    }
    // The popular searches and the categories: before he types, and when nothing was found.
    if (this.start) this.start.hidden = Boolean(answer) && !empty;
    if (!this.best) return;
    this.best.hidden = !empty;
    const row = this.best.querySelector('recs-rail');
    if (empty && row && !row.hasAttribute('data-wanted')) {
      row.setAttribute('data-wanted', '');
      document.dispatchEvent(new CustomEvent('recs:wanted', { detail: { place: this.best.dataset.recsPlace } }));
    }
  }

  /** Shopify's suggestions; where it has none for the page's language, its ordinary search. */
  address(terms) {
    const url = new URL(this.unavailable ? this.dataset.searchUrl : Kanze.routes.predictiveSearch, window.location.origin);
    url.searchParams.set('q', terms);
    if (this.unavailable) {
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
    } else {
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[limit_scope]', 'each');
    }
    url.searchParams.set('section_id', 'predictive-search');
    return url;
  }

  async search(typed) {
    this.pending?.abort();
    if (typed.length < 2) {
      this.show(null);
      return;
    }

    const { terms, changed } = words(typed);
    const controller = new AbortController();
    this.pending = controller;
    this.results.setAttribute('aria-busy', 'true');

    try {
      let response = await fetch(this.address(terms), { signal: controller.signal });
      // Shopify answers 417 when it has no suggestions for the page's language (Arabic, for
      // one). From then on the ordinary search is asked instead.
      if (response.status === 417 && !this.unavailable) {
        this.unavailable = true;
        response = await fetch(this.address(terms), { signal: controller.signal });
      }
      if (!response.ok) throw new Error(`Search request failed (${response.status})`);
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-predictive-results]');
      // He has typed on, or cleared the box, while this was on its way.
      if (this.pending !== controller) return;
      this.show(fresh, changed ? terms : '');
      // A search has settled when he has typed nothing more for a second and a half.
      window.clearTimeout(this.settled);
      this.settled = window.setTimeout(() => performed(typed, Number(fresh?.dataset.found) || 0, terms), 1500);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Kanze: search suggestions failed.', error);
        track('Search Failed', { error: /\((\d+)\)/.test(error.message || '') ? `status_${error.message.match(/\((\d+)\)/)[1]}` : error.name === 'TypeError' ? 'network' : 'unreadable' });
      }
    } finally {
      if (this.pending === controller) this.results.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
